import { createTheme, alpha } from '@mui/material/styles';

export const palette = {
  background: '#050816',
  surface: '#0B1224',
  surfaceTransparent: 'rgba(15,23,42,0.65)',
  primary: '#38BDF8',
  secondary: '#818CF8',
  success: '#22C55E',
  warning: '#F59E0B',
  danger: '#EF4444',
  text: '#F8FAFC',
  secondaryText: '#94A3B8',
};

export const glass = {
  border: `1px solid ${alpha(palette.primary, 0.14)}`,
  borderStrong: `1px solid ${alpha(palette.primary, 0.3)}`,
  blur: 'blur(22px) saturate(140%)',
  shadow: '0 18px 48px -18px rgba(2,8,23,0.9)',
  shadowHover: '0 26px 70px -22px rgba(2,8,23,0.95)',
};

export const statusColors = {
  online: palette.success,
  offline: palette.danger,
  updating: palette.warning,
  warning: palette.warning,
  critical: palette.danger,
  info: palette.primary,
  acknowledged: palette.secondary,
  resolved: palette.success,
  active: palette.danger,
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: palette.primary, light: '#7DD3FC', dark: '#0EA5E9' },
    secondary: { main: palette.secondary, light: '#A5B4FC', dark: '#6366F1' },
    success: { main: palette.success },
    warning: { main: palette.warning },
    error: { main: palette.danger },
    info: { main: palette.primary },
    background: {
      default: palette.background,
      paper: palette.surface,
    },
    text: {
      primary: palette.text,
      secondary: palette.secondaryText,
      disabled: 'rgba(148,163,184,0.45)',
    },
    divider: alpha(palette.primary, 0.12),
  },
  typography: {
    fontFamily: '"Outfit", "Inter", system-ui, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.5px' },
    h2: { fontWeight: 700, letterSpacing: '-0.4px' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, letterSpacing: '0.02em' },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.02em' },
    overline: { fontWeight: 700, letterSpacing: '0.16em' },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        html, body, #root {
          min-height: 100%;
        }

        body {
          background-color: ${palette.background};
          color: ${palette.text};
          overflow-x: hidden;
        }

        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: rgba(5,8,22,0.4); }
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, ${alpha(palette.primary, 0.45)}, ${alpha(palette.secondary, 0.35)});
          border-radius: 10px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        ::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, ${palette.primary}, ${palette.secondary}); background-clip: padding-box; }

        .holo-grid {
          background-image:
            linear-gradient(${alpha(palette.primary, 0.055)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(palette.primary, 0.055)} 1px, transparent 1px);
          background-size: 48px 48px;
        }

        .holo-scan {
          background: repeating-linear-gradient(
            to bottom,
            rgba(255,255,255,0.015) 0px,
            rgba(255,255,255,0.015) 1px,
            transparent 1px,
            transparent 4px
          );
        }

        @keyframes holoPulse {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50% { opacity: 0.75; transform: scale(1.04); }
        }
        @keyframes holoFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes holoEnter {
          from { opacity: 0; transform: translateY(14px) scale(0.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes holoSweep {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(220%); }
        }
        @keyframes holoSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes holoBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `,
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          border: glass.border,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '9px 18px',
          transition: 'transform 180ms ease, box-shadow 220ms ease, background-color 220ms ease, border-color 220ms ease',
          '&:hover': { transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(1px) scale(0.985)' },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${alpha(palette.primary, 0.95)}, ${alpha(palette.secondary, 0.85)})`,
          color: '#04070F',
          fontWeight: 700,
          boxShadow: `0 10px 26px -12px ${alpha(palette.primary, 0.85)}`,
          '&:hover': {
            boxShadow: `0 16px 34px -12px ${alpha(palette.primary, 0.95)}`,
            background: `linear-gradient(135deg, #7DD3FC, ${alpha(palette.secondary, 0.95)})`,
          },
        },
        outlined: {
          borderColor: alpha(palette.primary, 0.28),
          color: palette.text,
          background: alpha(palette.surface, 0.5),
          '&:hover': {
            borderColor: alpha(palette.primary, 0.55),
            background: alpha(palette.primary, 0.1),
            boxShadow: `0 10px 26px -16px ${alpha(palette.primary, 0.9)}`,
          },
        },
        containedSecondary: {
          background: alpha(palette.secondary, 0.16),
          color: '#C7D2FE',
          border: `1px solid ${alpha(palette.secondary, 0.35)}`,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'background-color 200ms ease, transform 200ms ease, box-shadow 220ms ease',
          '&:hover': {
            background: alpha(palette.primary, 0.12),
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: alpha('#0B1224', 0.55),
          transition: 'border-color 200ms ease, box-shadow 220ms ease, background-color 200ms ease',
          '& fieldset': { borderColor: alpha(palette.primary, 0.18) },
          '&:hover fieldset': { borderColor: alpha(palette.primary, 0.34) },
          '&.Mui-focused': {
            background: alpha('#0B1224', 0.75),
            boxShadow: `0 0 0 3px ${alpha(palette.primary, 0.12)}`,
          },
        },
        input: {
          fontFamily: '"Outfit", system-ui, sans-serif',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: palette.secondaryText },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          letterSpacing: '0.03em',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: alpha(palette.primary, 0.09),
          fontSize: '0.86rem',
        },
        head: {
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontSize: '0.68rem',
          color: palette.secondaryText,
          background: alpha('#050816', 0.45),
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          background: `linear-gradient(160deg, ${alpha('#0B1224', 0.97)}, ${alpha('#050816', 0.97)})`,
          border: glass.borderStrong,
          boxShadow: `0 40px 90px -30px rgba(0,0,0,0.95), 0 0 0 1px ${alpha(palette.primary, 0.08)}`,
          backdropFilter: 'blur(24px)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: `linear-gradient(160deg, ${alpha('#0B1224', 0.98)}, ${alpha('#050816', 0.98)})`,
          border: glass.border,
          backdropFilter: 'blur(20px)',
          boxShadow: glass.shadow,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: alpha('#020617', 0.94),
          border: `1px solid ${alpha(palette.primary, 0.2)}`,
          color: palette.text,
          fontSize: '0.72rem',
          letterSpacing: '0.04em',
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
          background: `linear-gradient(90deg, ${palette.primary}, ${palette.secondary})`,
          boxShadow: `0 0 12px ${alpha(palette.primary, 0.8)}`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          letterSpacing: '0.04em',
          color: palette.secondaryText,
          '&.Mui-selected': { color: palette.text },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 6,
          background: alpha(palette.primary, 0.1),
        },
        bar: {
          borderRadius: 6,
          background: `linear-gradient(90deg, ${palette.primary}, ${palette.secondary})`,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 42, height: 26, padding: 0 },
        switchBase: {
          padding: 3,
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: palette.primary,
            '& + .MuiSwitch-track': {
              opacity: 1,
              background: alpha(palette.primary, 0.35),
            },
          },
        },
        thumb: {
          width: 20,
          height: 20,
          boxShadow: `0 0 12px ${alpha(palette.primary, 0.6)}`,
        },
        track: { opacity: 1, background: alpha('#334155', 0.7), borderRadius: 20 },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: { color: palette.primary },
        thumb: {
          background: palette.text,
          boxShadow: `0 0 14px ${alpha(palette.primary, 0.9)}`,
        },
        rail: { background: alpha(palette.primary, 0.16) },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: { color: palette.primary },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${alpha(palette.primary, 0.2)}`,
          background: alpha('#0B1224', 0.8),
          backdropFilter: 'blur(16px)',
        },
      },
    },
  },
});

export default theme;
